import FeedbackRoundedIcon from '@mui/icons-material/FeedbackRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import {
    Avatar, Box, Button, ButtonBase, Divider, ListItemIcon, Menu, MenuItem,
    Snackbar, Stack, Typography,
} from '@mui/material';
import React from 'react';
import brandLockup from '../../../asset/logo/lockup-horizontal-color.svg';
import { BLUE, BLUE_HOVER, BLUE_TEXT, NAVIGATION_BACKGROUND, ERROR_TEXT, INTER, NAVY, POPPINS, RING_TRACK, SLATE, SURFACE_SOFT } from '../../student/studentStyles';

type NavItem = {
    label: string;
    icon: React.ElementType;
    page: StudentDesktopPage;
};

export type StudentDesktopPage = 'home' | 'courses' | 'pronunciation' | 'feedback' | 'profile' | 'settings';

const learningNav: NavItem[] = [
    { label: 'Home', icon: HomeRoundedIcon, page: 'home' },
    { label: 'My courses', icon: MenuBookRoundedIcon, page: 'courses' },
    { label: 'Pronunciation', icon: RecordVoiceOverRoundedIcon, page: 'pronunciation' },
    { label: 'Teacher feedback', icon: FeedbackRoundedIcon, page: 'feedback' },
];

function NavButton({ item, active, onSelect }: { item: NavItem; active: boolean; onSelect: (item: NavItem) => void }) {
    return (
        <ButtonBase
            onClick={() => onSelect(item)}
            aria-current={active ? 'page' : undefined}
            sx={{
                width: '100%', minHeight: 46, px: 1.5, gap: 1.5, justifyContent: 'flex-start',
                borderRadius: '12px', color: active ? BLUE_TEXT : NAVY,
                backgroundColor: active ? BLUE_HOVER : 'transparent',
                boxShadow: active ? `inset 3px 0 0 ${BLUE}` : 'none',
                '&:hover': { backgroundColor: BLUE_HOVER },
            }}
        >
            <item.icon sx={{ fontSize: 21, color: BLUE }} />
            <Typography sx={{ fontFamily: INTER, fontWeight: active ? 700 : 600, fontSize: 13.5 }}>{item.label}</Typography>
        </ButtonBase>
    );
}

interface StudentDesktopShellProps {
    children: React.ReactNode;
    activePage: StudentDesktopPage;
    studentName: string;
    avatarColor: string;
    notice: string;
    onNavigate: (page: StudentDesktopPage) => void;
    onNotice: (message: string) => void;
    onCloseNotice: () => void;
}

export default function StudentDesktopShell({
    children, activePage, studentName, avatarColor, notice, onNavigate, onNotice, onCloseNotice,
}: StudentDesktopShellProps) {
    const [profileAnchor, setProfileAnchor] = React.useState<HTMLElement | null>(null);

    const selectNav = (item: NavItem) => {
        onNavigate(item.page);
    };

    return (
        <Box sx={{ minHeight: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: SURFACE_SOFT }}>
            <Box component="header" sx={{ height: 72, flexShrink: 0, px: { xs: 2, md: 3, xl: 4 }, display: 'flex', alignItems: 'center', backgroundColor: '#fff', borderBottom: `1px solid ${RING_TRACK}`, zIndex: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1.4}>
                    <Box component="img" src={brandLockup} alt="VerbaSchola" sx={{ height: 25, width: 'auto', display: 'block' }} />
                    <Box sx={{ width: '1px', height: 20, backgroundColor: RING_TRACK }} />
                    <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, color: SLATE, letterSpacing: '.08em', textTransform: 'uppercase' }}>Student</Typography>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1.25} sx={{ ml: 'auto' }}>
                    <Button
                        onClick={event => setProfileAnchor(event.currentTarget)}
                        aria-haspopup="menu"
                        aria-expanded={Boolean(profileAnchor)}
                        sx={{ minWidth: 0, p: 0.65, borderRadius: '12px', textTransform: 'none', '&:hover': { backgroundColor: SURFACE_SOFT } }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar sx={{ width: 38, height: 38, bgcolor: avatarColor, fontFamily: POPPINS, fontWeight: 600, fontSize: 14 }}>{studentName.charAt(0).toUpperCase()}</Avatar>
                            <Box sx={{ textAlign: 'left', display: { xs: 'none', sm: 'block' } }}>
                                <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12.5, color: NAVY, lineHeight: 1.2 }}>{studentName}</Typography>
                                <Typography sx={{ fontFamily: INTER, fontSize: 11, color: SLATE }}>Student</Typography>
                            </Box>
                            <KeyboardArrowDownRoundedIcon sx={{ color: SLATE, fontSize: 19 }} />
                        </Stack>
                    </Button>
                </Stack>

                <Menu
                    anchorEl={profileAnchor}
                    open={Boolean(profileAnchor)}
                    onClose={() => setProfileAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    PaperProps={{ sx: { minWidth: 205, mt: 0.75, border: `1px solid ${RING_TRACK}`, borderRadius: '14px', boxShadow: '0 12px 30px rgba(22, 58, 99, 0.12)' } }}
                >
                    {[
                        { label: 'Profile', icon: PersonRoundedIcon, page: 'profile' as StudentDesktopPage },
                        { label: 'Settings', icon: SettingsRoundedIcon, page: 'settings' as StudentDesktopPage },
                    ].map(item => (
                        <MenuItem key={item.label} onClick={() => { setProfileAnchor(null); onNavigate(item.page); }} sx={{ minHeight: 43, fontFamily: INTER, fontSize: 13, color: NAVY }}>
                            <ListItemIcon sx={{ minWidth: '34px !important', color: SLATE }}><item.icon sx={{ fontSize: 20 }} /></ListItemIcon>
                            {item.label}
                        </MenuItem>
                    ))}
                    <MenuItem onClick={() => { setProfileAnchor(null); onNotice('Help & support is not included in this mockup pass.'); }} sx={{ minHeight: 43, fontFamily: INTER, fontSize: 13, color: NAVY }}>
                        <ListItemIcon sx={{ minWidth: '34px !important', color: SLATE }}><HelpOutlineRoundedIcon sx={{ fontSize: 20 }} /></ListItemIcon>
                        Help &amp; support
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={() => { setProfileAnchor(null); onNotice('Log out is disabled in this static prototype.'); }} sx={{ minHeight: 44, fontFamily: INTER, fontSize: 13, color: ERROR_TEXT }}>
                        <ListItemIcon sx={{ minWidth: '36px !important', color: ERROR_TEXT }}><LogoutRoundedIcon sx={{ fontSize: 20 }} /></ListItemIcon>
                        Log out
                    </MenuItem>
                </Menu>
            </Box>

            <Stack direction="row" spacing={0.75} sx={{ display: { xs: 'flex', md: 'none' }, flexShrink: 0, px: 2, py: 1, overflowX: 'auto', backgroundColor: NAVIGATION_BACKGROUND, borderBottom: `1px solid ${RING_TRACK}` }}>
                {learningNav.map(item => (
                    <Button key={item.label} onClick={() => selectNav(item)} startIcon={<item.icon />} sx={{ flexShrink: 0, color: item.page === activePage ? BLUE_TEXT : NAVY, backgroundColor: item.page === activePage ? BLUE_HOVER : 'transparent', boxShadow: item.page === activePage ? `inset 0 -3px 0 ${BLUE}` : 'none', '& .MuiButton-startIcon': { color: BLUE }, '&:hover': { backgroundColor: BLUE_HOVER }, fontFamily: INTER, fontWeight: 700, fontSize: 12, textTransform: 'none' }}>{item.label}</Button>
                ))}
            </Stack>

            <Box sx={{ minHeight: 0, flex: 1, display: 'flex' }}>
                <Box component="aside" sx={{ width: { md: 232, xl: 248 }, flexShrink: 0, py: 3, px: 2, display: { xs: 'none', md: 'flex' }, flexDirection: 'column', backgroundColor: NAVIGATION_BACKGROUND, borderRight: `1px solid ${RING_TRACK}` }}>
                    <Typography sx={{ px: 1.5, mb: 1.25, fontFamily: INTER, fontWeight: 700, fontSize: 11, letterSpacing: '.1em', color: SLATE }}>LEARN</Typography>
                    <Stack spacing={0.75}>
                        {learningNav.map(item => <NavButton key={item.label} item={item} active={item.page === activePage} onSelect={selectNav} />)}
                    </Stack>

                </Box>

                <Box id="student-desktop-content" component="main" sx={{ flex: 1, minWidth: 0, overflowY: 'auto', backgroundColor: SURFACE_SOFT }}>
                    {children}
                </Box>
            </Box>

            <Snackbar open={Boolean(notice)} autoHideDuration={3200} onClose={onCloseNotice} message={notice} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} ContentProps={{ sx: { borderRadius: '12px', backgroundColor: NAVY, fontFamily: INTER, fontSize: 13 } }} />
        </Box>
    );
}
